import {
    Box,
    ClickAwayListener,
    IconButton,
    Paper,
    Popper,
    Typography,
} from '@mui/material';
import { Repeat } from 'lucide-react';
import { useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import AvailableImageItem from '~/components/ImageItem/AvailableImageItem';
import ImageItem from '~/components/ImageItem/ImageItem';
import { Image3D } from '~/domain/patient/image3d';
import { AvailableImage } from '~/hooks/image/usePrimaryImage';

type PrimaryImageHeaderProps = {
    primaryImage: Image3D;
    availableImages: AvailableImage[];
    onChangePrimaryImage: (image: AvailableImage) => void;
};

export default function PrimaryImageHeader({
    primaryImage,
    availableImages,
    onChangePrimaryImage,
}: PrimaryImageHeaderProps) {
    const intl = useIntl();
    const [isOpen, setIsOpen] = useState(false);
    const headerRef = useRef<HTMLDivElement>(null);

    const hasMultipleImages = availableImages.length > 1;

    const handleToggle = () => {
        setIsOpen(!isOpen);
    };

    const handleClose = () => {
        setIsOpen(false);
    };

    const handleClickAway = (event: MouseEvent | TouchEvent) => {
        const target = event.target as HTMLElement;
        if (
            target === document.body ||
            target.closest('[role="presentation"]') ||
            target.closest('[role="dialog"]')
        ) {
            return;
        }
        handleClose();
    };

    return (
        <>
            <Box
                ref={headerRef}
                position="relative"
                data-testid="primary-image-header"
            >
                <Box>
                    <Box
                        display="flex"
                        justifyContent="space-between"
                        alignItems="center"
                        px={1.5}
                        pb={1}
                    >
                        <Typography
                            fontSize={12}
                            color="primary"
                            fontWeight={500}
                        >
                            <FormattedMessage id="workflow.imageRegistration.primaryImage" />
                        </Typography>
                        {hasMultipleImages && (
                            <IconButton
                                size="small"
                                onClick={handleToggle}
                                selected={isOpen}
                                sx={{
                                    padding: 0,
                                    color: 'primary.main',
                                    marginY: -0.5,
                                }}
                                data-testid="change-primary-image-button"
                                aria-label={intl.formatMessage({
                                    id: 'changePrimaryImage',
                                })}
                            >
                                <Repeat size={12} />
                            </IconButton>
                        )}
                    </Box>
                    <ImageItem image={primaryImage} isSelected />
                </Box>
            </Box>

            {hasMultipleImages && (
                <Popper
                    open={isOpen}
                    anchorEl={headerRef.current}
                    placement="right-start"
                    sx={{ zIndex: 1300 }}
                    modifiers={[
                        {
                            name: 'offset',
                            options: { offset: [-9, 1] },
                        },
                    ]}
                >
                    <ClickAwayListener onClickAway={handleClickAway}>
                        <Paper
                            elevation={0}
                            sx={(theme) => ({
                                bgcolor: theme.palette.background.default,
                                border: `1px solid ${theme.palette.divider}`,
                                borderLeft: 'none',
                                borderRadius: 0,
                            })}
                        >
                            <Box
                                display="flex"
                                flexDirection="column"
                                maxHeight={300}
                                overflow="auto"
                            >
                                {availableImages.map((img) => (
                                    <AvailableImageItem
                                        key={
                                            img.imageObject?.dicomUid ||
                                            img.seriesInstanceUID
                                        }
                                        image={img}
                                        isSelected={
                                            primaryImage.seriesUid ===
                                            img.seriesInstanceUID
                                        }
                                        onSelect={() => {
                                            onChangePrimaryImage(img);
                                            handleClose();
                                        }}
                                    />
                                ))}
                            </Box>
                        </Paper>
                    </ClickAwayListener>
                </Popper>
            )}
        </>
    );
}
